/**
 * Resample OHLC arrays to larger bar periods (TradingView-style timeframes).
 * Source bars must be no finer than the detected step; requested periods finer than
 * the source are clamped (cannot invent sub-minute bars).
 */

/** Use with `timeframeSecondsFromId` / `resolvedBarPeriodSeconds` when `id === CUSTOM_TIMEFRAME_ID`. */
export const CUSTOM_TIMEFRAME_ID = "custom";

export const TIMEFRAME_OPTIONS = [
  { id: "1s", seconds: 1, label: "1s" },
  { id: "5s", seconds: 5, label: "5s" },
  { id: "1m", seconds: 60, label: "1m" },
  { id: "3m", seconds: 180, label: "3m" },
  { id: "5m", seconds: 300, label: "5m" },
  { id: "15m", seconds: 900, label: "15m" },
  { id: "30m", seconds: 1800, label: "30m" },
  { id: "1h", seconds: 3600, label: "1h" },
  { id: "2h", seconds: 7200, label: "2h" },
  { id: "4h", seconds: 14400, label: "4h" },
  { id: "1d", seconds: 86400, label: "1D" },
  { id: "1w", seconds: 604800, label: "1W" },
  { id: CUSTOM_TIMEFRAME_ID, label: "Custom" },
];

/** Unit for custom bar length (UI). */
export const CUSTOM_TIMEFRAME_UNITS = [
  { id: "s", label: "sec", multiplier: 1 },
  { id: "m", label: "min", multiplier: 60 },
  { id: "h", label: "hours", multiplier: 3600 },
  { id: "d", label: "days", multiplier: 86400 },
  { id: "w", label: "weeks", multiplier: 604800 },
];

/**
 * Converts custom numeric input + unit to seconds (minimum 1).
 */
export function customTimeframeToSeconds(valueStr, unitId) {
  const n = parseFloat(String(valueStr ?? "").trim(), 10);
  if (!Number.isFinite(n) || n <= 0) {
    return 300;
  }
  const row = CUSTOM_TIMEFRAME_UNITS.find((u) => u.id === unitId);
  const mult = row ? row.multiplier : 60;
  return Math.max(1, Math.floor(n * mult));
}

export function timeframeSecondsFromId(id, customSeconds) {
  if (id === CUSTOM_TIMEFRAME_ID) {
    return Number.isFinite(customSeconds) && customSeconds > 0 ? customSeconds : 300;
  }
  const row = TIMEFRAME_OPTIONS.find((o) => o.id === id);
  return row && typeof row.seconds === "number" ? row.seconds : 300;
}

/**
 * Median spacing between consecutive bar times (seconds). Defaults to 60 if unknown.
 */
export function estimateMedianBarStepSeconds(candles) {
  if (!candles || candles.length < 2) {
    return 60;
  }
  const diffs = [];
  for (let i = 1; i < candles.length; i += 1) {
    const a = Number(candles[i].time);
    const b = Number(candles[i - 1].time);
    if (Number.isFinite(a) && Number.isFinite(b) && a > b) {
      diffs.push(a - b);
    }
  }
  if (!diffs.length) {
    return 60;
  }
  diffs.sort((x, y) => x - y);
  const mid = Math.floor(diffs.length / 2);
  const m = diffs.length % 2 ? diffs[mid] : (diffs[mid - 1] + diffs[mid]) / 2;
  return Number.isFinite(m) && m > 0 ? m : 60;
}

/**
 * Effective bar period: at least source resolution, at least requested timeframe.
 * When `timeframeId` is {@link CUSTOM_TIMEFRAME_ID}, pass `customSeconds` from {@link customTimeframeToSeconds}.
 */
export function resolvedBarPeriodSeconds(candles, timeframeId, customSeconds) {
  const want = timeframeSecondsFromId(timeframeId, customSeconds);
  const step = estimateMedianBarStepSeconds(candles);
  return Math.max(want, step);
}

/**
 * Bucket OHLC into fixed-length periods (Unix `time` is bucket open).
 */
export function aggregateOhlcToPeriod(candles, periodSeconds) {
  if (!candles.length || !(periodSeconds > 0)) {
    return candles;
  }

  const sorted = [...candles].sort(
    (a, b) => Number(a.time) - Number(b.time)
  );
  const buckets = new Map();

  for (const c of sorted) {
    const t = Number(c.time);
    if (!Number.isFinite(t)) {
      continue;
    }
    const bucketStart = Math.floor(t / periodSeconds) * periodSeconds;
    let row = buckets.get(bucketStart);
    if (!row) {
      row = {
        time: bucketStart,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      };
      if (Number.isFinite(c.volume)) {
        row.volume = c.volume;
      }
      buckets.set(bucketStart, row);
    } else {
      row.high = Math.max(row.high, c.high);
      row.low = Math.min(row.low, c.low);
      row.close = c.close;
      if (Number.isFinite(c.volume)) {
        row.volume = (Number.isFinite(row.volume) ? row.volume : 0) + c.volume;
      }
    }
  }

  return Array.from(buckets.values()).sort((a, b) => a.time - b.time);
}
